// 文体の経年変化 — 期間別の文体特徴を AI が観察
import { escapeHtml, screenHead, showScreen, showLoading, hideLoading, toast, renderHistorySection, bindHistoryHandlers } from '../ui.js';
import { fetchStyleEvolution } from '../api.js';
import { getAllBooks, saveAiHistory, getAiHistoryByType } from '../cache.js';

export async function showStyleEvolutionScreen() {
  const histItems = await getAiHistoryByType('style-evolution');
  renderEntry(histItems);
}

function renderEntry(histItems) {
  const html = `
    ${screenHead('文体の経年変化')}
    <p style="color:var(--dim);font-size:13px;line-height:1.75;margin:0 0 20px;">
      半年または年ごとに区切ったメモから、文体の経年変化を AI が観察します。<br/>
      簡潔↔内省、外的描写↔内的探求、論理↔詩的などの傾向の推移が見えます。
    </p>
    <button class="btn btn--ink btn--block" id="btn-go" type="button">
      <svg class="icon"><use href="#i-sparkles"/></svg>
      <span>文体の推移を観察する</span>
    </button>
    <p style="margin-top:14px;font-size:11px;color:var(--mute);line-height:1.6;font-family:var(--font-mono);letter-spacing:0.08em;">
      使用モデル: claude-opus-5-5 · 1 回 約 5-12 円
    </p>
    ${renderHistorySection(histItems)}
  `;
  const screen = showScreen('style-evolution', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-go').addEventListener('click', runStyle);
  bindHistoryHandlers(
    screen,
    histItems,
    (entry) => renderResult(entry.payload),
    () => showStyleEvolutionScreen()
  );
}

// 6 ヶ月ごとに区切って各期間の書籍を集める。endDate がある書籍だけ対象。
function periodize(books) {
  const eligible = books.filter((b) => (b.note || '').trim().length > 100 && b.endDate);
  if (eligible.length === 0) return [];
  eligible.sort((a, b) => (a.endDate || '').localeCompare(b.endDate || ''));
  const periods = new Map();
  for (const b of eligible) {
    const d = new Date(b.endDate);
    const y = d.getFullYear();
    const half = d.getMonth() < 6 ? '上' : '下';
    const key = `${y}年${half}半期`;
    if (!periods.has(key)) periods.set(key, []);
    periods.get(key).push(b);
  }
  return [...periods.entries()].map(([label, list]) => ({ label, books: list }));
}

async function runStyle() {
  const all = await getAllBooks();
  const periods = periodize(all);
  if (periods.length < 2) {
    toast('期間別に分けるには複数期間にまたがる読了書籍が必要です');
    return;
  }
  showLoading('AI が文体を観察中…');
  try {
    const payload = periods.map((p) => ({
      label: p.label,
      books: p.books.map((b) => ({ name: b.name, endDate: b.endDate, note: b.note })),
    }));
    const result = await fetchStyleEvolution(payload);
    await saveAiHistory({
      type: 'style-evolution',
      title: `文体推移 (${periods.length} 期間)`,
      subtitle: result.overall_arc?.slice(0, 50) || '',
      context: { periods: periods.length },
      payload: result,
    });
    renderResult(result);
  } catch (e) {
    console.error(e);
    toast('観察に失敗しました');
  } finally {
    hideLoading();
  }
}

export function renderFromHistory(entry) {
  renderResult(entry.payload);
}

function renderResult(data) {
  const eras = data.eras || [];
  const html = `
    ${screenHead('文体の経年変化')}
    ${data.overall_arc ? `
      <section style="margin:0 0 24px;padding:14px 16px;border:1px solid var(--line);background:var(--paper);">
        <p style="margin:0 0 6px;font-family:var(--font-mono);font-size:10px;letter-spacing:0.22em;color:var(--mute);">overall arc</p>
        <p style="margin:0;font-family:var(--font-serif);font-size:14px;line-height:1.95;color:var(--ink);">${escapeHtml(data.overall_arc)}</p>
      </section>
    ` : ''}
    ${eras.map((e, i) => `
      <article class="era-card">
        <p class="era-card__order">期 ${i + 1}</p>
        <h3 class="era-card__label">${escapeHtml(e.label || '')}</h3>
        <p class="era-card__summary">${escapeHtml(e.summary || '')}</p>
        ${(e.traits || []).length ? `<div class="era-card__traits">${(e.traits || []).map((t) => `<span class="chip">${escapeHtml(t)}</span>`).join('')}</div>` : ''}
      </article>
    `).join('')}
    <button class="btn btn--block btn--ghost" id="btn-regen" type="button" style="margin-top:24px;">
      <svg class="icon"><use href="#i-refresh"/></svg>
      <span>再観察する</span>
    </button>
  `;
  const screen = showScreen('style-evolution', html);
  screen.querySelector('[data-action="back"]').addEventListener('click', () => window.history.back());
  screen.querySelector('#btn-regen').addEventListener('click', () => showStyleEvolutionScreen());
}
